import type {
  NextPage,
  InferGetServerSidePropsType,
  GetServerSidePropsContext,
} from 'next';

import { ShelvesData, ShelvesDataProps } from 'types';

import { backendFunction } from '~/clients/firebase/client';
import Firebase from '@/clients/firebase/firebase-react-query';

import { useGlobalState } from '~/state';
import { ActionType } from '~/state/types';
import { withUser } from '~/lib/user';
import useShelves from '~/lib/hooks/use-shelves';

import CreateShelfModal from '~/components/shelves/create-shelf-modal';
import UserProfileImage from '~/components/user-profile-image';
import Button from '~/components/button';
import PreferenceModal from '~/components/shelves/preference-modal';
import ShelfTile from '~/components/tiles/shelf';
import Callout from '~/components/callout';

import styles from '~/styles/pages/shelves.module.scss';
import COLORS from '~/lib/helpers/color-helper';
import ShelfIcon from '~/assets/svg/icons/shelf-icon';
import PlusSVG from '~/assets/svg/icons/plus';
import InfoSVG from '~/assets/svg/icons/info';
import PageMetaData from '~/components/page-meta-data';
import Spacer from '~/components/spacer';

const ShelvesPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ shelvesData, username }) => {
  const { state: { user, shelves: { data: { preference } } }, dispatch } = useGlobalState();
  const { setPreference } = useShelves();

  const {
    data: { user: shelvesUser, shelves, isOwner: IS_OWNER },
    refetch
  } = Firebase<ShelvesData>().backendFunction(
    'shelves-getUserShelves',
    { request_user_uid: user.data.uid, username },
    { initialData: shelvesData }
  );

  const renderUser = () => {
    return (
      <div className={styles.user}>
        <div className={styles['user-image']}>
          <UserProfileImage user={shelvesUser} link />
        </div>
        <div className={styles['user-info']}>
          <h4>{shelvesUser?.name}</h4>
          <p>@{shelvesUser?.username}</p>
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    const handleCreateNewShelf = (e: any) => {
      dispatch({
        type: ActionType.ADD_MODAL_DATA,
        payload: {
          title: 'Create a new shelf',
          component: <CreateShelfModal callback={refetch} />,
        },
      });
    };

    return (
      <div className={styles['header']}>
        <div className={styles['header-title']}>
          {IS_OWNER ? (
            <h1>My Shelves</h1>
          ) : (
            <h1>Shelves by {shelvesUser?.name}</h1>
          )}

          {IS_OWNER && (
            <div>
              <Button
                icon={<PlusSVG fillColor={COLORS.foreground} />}
                shape='circle'
                size='small'
                onPress={handleCreateNewShelf}
              />
            </div>
          )}
        </div>
        <div className={styles['header-subtext']}>
          {IS_OWNER ? (
            <p>
              My curated list of bookshelves to organize all my favorite books
            </p>
          ) : (
            <p>
              {`${shelvesUser?.name}'s curated list of bookshelves to organize all their favorite books`}
            </p>
          )}
        </div>
        {!IS_OWNER && renderUser()}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles['empty-state']}>
      <ShelfIcon width={250} height={250} />
      <h2>
        {IS_OWNER
          ? 'Looks like you don\'t have any shelves yet'
          : 'No shelves to see here'}
      </h2>
      <p>
        {IS_OWNER
          ? 'Start organizing your favorite books and create personalized shelves'
          : 'This person hasn\'t created any shelves yet. Check back soon!'}
      </p>
    </div>
  );

  const renderGrid = () => {
    return (
      <div className={styles['grid']}>
        {shelves?.map((shelf: any) => {
          return (
            <ShelfTile
              key={`${shelf.id}-${username}`}
              shelf={shelf}
              username={username as string}
            />
          );
        })}
      </div>
    );
  };

  const renderPreference = () => {
    const handlePreference = () => {
      dispatch({
        type: ActionType.ADD_MODAL_DATA,
        payload: {
          title: 'Change default preference',
          component: (
            <PreferenceModal
              setPreference
              shelves={shelves}
              onSave={async ({
                pinned_copy,
                shelf_id,
                shelf_title,
              }: {
                pinned_copy: boolean;
                shelf_id: string;
                shelf_title: string;
              }) => {
                setPreference({ pinned_copy, shelf_id, shelf_title });
                dispatch({ type: ActionType.FLUSH_MODAL_DATA });
                dispatch({
                  type: ActionType.ADD_SNACK_DATA,
                  payload: {
                    title: 'Preference saved',
                    type: 'success',
                  },
                });
              }}
            />
          ),
        },
      });
    };

    return (
      <div className={styles['preference']}>
        <div className={styles['preference-text']}>
          <InfoSVG fillColor={COLORS.grey} />
          <p>
            All books are saved as{' '}
            <span>{preference?.pinned_copy ? 'Specific Copies' : 'Titles'}</span>{' '}
            to{' '}
            <span>{` ${preference?.shelf_title || 'Wishlist'}`}</span>{' '}
            by default
          </p>
        </div>

        <p onClick={handlePreference} className={styles['preference-link']}>
          Change Preferences
        </p>
      </div>
    );
  };

  const renderIdeas = () => {
    const ideas = [
      { title: 'TBR (To Be Read)', description: 'Books I want to read', id: 'tbr' },
      {
        title: 'All Time Favs',
        description: 'Books I love',
        id: 'all-time-favs',
      },
      {
        title: 'Recent Reads',
        description: 'Books I just finished',
        id: 'recent-reads',
      },
      {
        title: 'Holiday Reading',
        description: 'Books I want to read during the holidays',
        id: 'holiday-reading',
      },
    ];

    const handleCreateShelf = (e: any, idea: any) => {
      dispatch({
        type: ActionType.ADD_MODAL_DATA,
        payload: {
          title: 'Create a new shelf',
          component: (
            <CreateShelfModal initData={{ ...idea }} callback={refetch} />
          ),
        },
      });
    };

    return (
      <div className={styles['ideas']}>
        <h2>Looking for ideas?</h2>
        <p>Here are same popular ideas for you to create more shelves</p>
        <div className={styles['ideas-container']}>
          {ideas.map((idea) => {
            return (
              <div
                key={idea.id}
                className={styles['ideas-card']}
                onClick={(e) => handleCreateShelf(e, idea)}
              >
                <div className={styles['ideas-image']}>
                  <div style={{ height: 50, width: 50 }}>
                    <PlusSVG />
                  </div>
                </div>
                <h3>{idea.title}</h3>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    //add page meta data
    <>
      <PageMetaData
        title={`${shelvesUser?.name} Shelves | Pango Books`}
        description={`Check out ${shelvesUser?.name} shelves on Pango Books`}
      />
      <div className={styles['content-container']}>
        {renderHeader()}
        {shelves?.length > 0 ? renderGrid() : renderEmptyState()}
        {IS_OWNER && renderPreference()}
        {IS_OWNER && renderIdeas()}
        <Spacer />
        <Spacer />
        <Callout
          title="Shelves from the Pango Community"
          message="Browse through shelves curated by the Pango community to find your next favorite book"
          style='tertiary'
          button={{
            text: 'Explore Shelves',
            link: {
              href: '/shelves',
            },
          }}

        />
      </div>
    </>
  );
};

//get server side props
//use withUser to get uid and pass as data
//if userId is stale then just default to client with firebase function inside of react query
export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  try {
    const authUser = await withUser(context);
    const request_user_uid = authUser?.props?.user?.uid;
    const { username } = context.query;

    const shelvesData = await backendFunction<ShelvesData, ShelvesDataProps>('shelves-getUserShelves', {
      request_user_uid,
      username: username as string,
    });

    return {
      props: {
        shelvesData,
        username: username as string,
      }
    };
  } catch (e: any) {
    return { notFound: true };
  }
};

export default ShelvesPage;
